import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Avatar,
  Box,
  Card,
  Button,
  Chip,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
  Alert,
  Tabs,
  Tab,
  Skeleton,
  alpha,

  Tooltip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import EditIcon from "@mui/icons-material/Edit";
import FolderIcon from "@mui/icons-material/Folder";
import CameraAltRoundedIcon from "@mui/icons-material/CameraAltRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ShieldMoonRoundedIcon from "@mui/icons-material/ShieldMoonRounded";
import VerifiedRoundedIcon from "@mui/icons-material/VerifiedRounded";

import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";

import { baseUrl } from "../../constants";
import { getUserInfoFromToken } from "../../utils/Main";

/** ---------------- Types ---------------- */
type DepartmentGroup = {
  department: { id: number; name: string; workspaceId: number };
  positions?: { id: number; name: string }[];
  projects?: { id: number; name: string }[];
};

type ProfileShape = {
  name?: string;
  title?: string;
  age?: number | string;
  gender?: string;
  email?: string;
  phone?: string;
  address?: string;
  notes?: string;
  dateOfBirth?: string | null;
  imageUrl?: string;
};

type AssignmentsPayload = {
  profile?: ProfileShape;
  positionsGroupedByDepartment?: DepartmentGroup[];
  projectsGroupedByDepartment?: DepartmentGroup[];
  username?: string;
};

/** -------------- Helpers -------------- */
const normalizeImageUrl = (url?: string) => {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  return `${baseUrl.replace(/\/+$/, "")}/${String(url).replace(/^\/+/, "")}`;
};

const defaultProfile: Required<ProfileShape> = {
  name: "",
  title: "",
  age: "",
  gender: "",
  email: "",
  phone: "",
  address: "",
  notes: "",
  dateOfBirth: null,
  imageUrl: "",
};

/** -------------- Component -------------- */
const Profile: React.FC = () => {
  const theme = useTheme();

  const [user, setUser] = useState<AssignmentsPayload | null>(null);
  const [dateOfBirth, setDateOfBirth] = useState<Dayjs | null>(null);

  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [hasAttemptedFetch, setHasAttemptedFetch] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [tab, setTab] = useState(0);
  const [isEditing, setIsEditing] = useState(false);

  const [selectedPositionDept, setSelectedPositionDept] =
    useState<string>("all");
  const [selectedProjectDept, setSelectedProjectDept] =
    useState<string>("all");

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info" | "warning";
  }>({ open: false, message: "", severity: "success" });

  /** -------- Fetch user assignments (same endpoint used across app) -------- */
  const fetchUser = async (signal?: AbortSignal) => {
    const token = localStorage.getItem("accessToken");
    const userId = getUserInfoFromToken()?.id;

    try {
      setLoading(true);
      setHasError(false);
      setHasAttemptedFetch(true);
      const res = await axios.get<AssignmentsPayload>(
        `${baseUrl}/users/${userId}/assignments`,
        { headers: { Authorization: `Bearer ${token}` }, signal }
      );

      const raw = res.data || {};
      
      // Ensure we always have a valid user object, even if profile is null
      if (!raw || typeof raw !== 'object' || !('id' in raw) || !raw.id) {
        throw new Error("Invalid user data received");
      }
      
      const normalized: AssignmentsPayload = {
        ...raw,
        profile: raw.profile ? {
          ...defaultProfile,
          ...raw.profile,
        } : defaultProfile,
        positionsGroupedByDepartment: Array.isArray(
          raw.positionsGroupedByDepartment
        )
          ? raw.positionsGroupedByDepartment
          : [],
        projectsGroupedByDepartment: Array.isArray(
          raw.projectsGroupedByDepartment
        )
          ? raw.projectsGroupedByDepartment
          : [],
      };

      const possibleImg =
        raw?.profile?.imageUrl ??
        (raw as { avatarUrl?: string; imageUrl?: string })?.avatarUrl ??
        (raw as { avatarUrl?: string; imageUrl?: string })?.imageUrl ??
        "";
      normalized.profile!.imageUrl = normalizeImageUrl(possibleImg);

      setUser(normalized);

      if (normalized.profile?.dateOfBirth) {
        setDateOfBirth(dayjs(normalized.profile.dateOfBirth));
      } else {
        setDateOfBirth(null);
      }
    } catch (err: unknown) {
      if (axios.isCancel(err)) return;
      console.error(err);
      setHasError(true);
      setSnackbar({
        open: true,
        message:
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message || "Failed to load profile information.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const ac = new AbortController();
    fetchUser(ac.signal);
    return () => ac.abort();
  }, []);

  /** -------- Derived counts -------- */
  const positionsByDept = useMemo(
    () => user?.positionsGroupedByDepartment ?? [],
    [user?.positionsGroupedByDepartment]
  );
  
  const projectsByDept = useMemo(
    () => user?.projectsGroupedByDepartment ?? [],
    [user?.projectsGroupedByDepartment]
  );

  const totalPositions = useMemo(
    () =>
      positionsByDept.reduce(
        (sum, g) => sum + (g.positions?.length || 0),
        0
      ),
    [positionsByDept]
  );

  const totalProjects = useMemo(
    () =>
      projectsByDept.reduce(
        (sum, g) => sum + (g.projects?.length || 0),
        0
      ),
    [projectsByDept]
  );

  const uniqueDepartmentsCount = useMemo(() => {
    const set = new Set<number>();
    positionsByDept.forEach((g) => set.add(g.department.id));
    projectsByDept.forEach((g) => set.add(g.department.id));
    return set.size;
  }, [positionsByDept, projectsByDept]);

  /** -------- Inline editing handlers -------- */
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (!user) return;
    const { name, value } = e.target;
    setUser({
      ...user,
      profile: { ...(user.profile || defaultProfile), [name]: value },
    });
  };

  const handleAvatarClick = () => {
    if (isEditing && fileInputRef.current) fileInputRef.current.click();
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length) return;

    const file = e.target.files[0];
    // Soft validation
    const allowed = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
    if (!allowed.includes(file.type)) {
      setSnackbar({
        open: true,
        message: "Please choose a PNG/JPEG/WEBP image.",
        severity: "warning",
      });
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      setSnackbar({
        open: true,
        message: "Image is too large (max 4MB).",
        severity: "warning",
      });
      return;
    }

    setSelectedImage(file);
    setUser((prev) =>
      prev
        ? {
            ...prev,
            profile: {
              ...(prev.profile || defaultProfile),
              imageUrl: URL.createObjectURL(file),
            },
          }
        : prev
    );
  };

  /** -------- Submit changes -------- */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    // Optional: allow empty DoB; enforce minimal fields
    if (!user.profile?.name) {
      setSnackbar({
        open: true,
        message: "Name is required.",
        severity: "warning",
      });
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      const profile = user.profile || defaultProfile;

      formData.append("name", profile.name || "");
      formData.append("title", profile.title || "");
      formData.append("age", profile.age?.toString() || "");
      formData.append("gender", profile.gender || "");
      formData.append("email", profile.email || "");
      formData.append("phone", profile.phone || "");
      formData.append("address", profile.address || "");
      formData.append("notes", profile.notes || "");
      // Only send DoB if selected (API accepts string)
      if (dateOfBirth) {
        formData.append("dateOfBirth", dateOfBirth.toISOString());
      }

      if (selectedImage) {
        formData.append("image", selectedImage);
      }

      const token = localStorage.getItem("accessToken");
      await axios.patch(`${baseUrl}/profile`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setSnackbar({
        open: true,
        message: "Profile updated successfully.",
        severity: "success",
      });

      setSelectedImage(null);
      setIsEditing(false);
      // Re-fetch to get canonical URLs from server
      fetchUser();
    } catch (err: unknown) {
      console.error("Update failed:", (err as { response?: { data?: unknown } })?.response?.data || err);
      setSnackbar({
        open: true,
        message:
          "Failed to update: " +
          ((err as { response?: { data?: { message?: string } } })?.response?.data?.message || "Unexpected error"),
        severity: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  /** -------- Render helpers -------- */
  const AssignmentSection = ({
    title,
    data,
    type,
    selectedDept,
    onFilterChange,
  }: {
    title: string;
    data: DepartmentGroup[];
    type: "positions" | "projects";
    selectedDept: string;
    onFilterChange: (dept: string) => void;
  }) => {
    const deptOptions = useMemo(
      () => ["all", ...new Set(data.map((g) => g.department.name))],
      [data]
    );

    const filtered = useMemo(
      () =>
        selectedDept === "all"
          ? data
          : data.filter((g) => g.department.name === selectedDept),
      [data, selectedDept]
    );

    const totalItems = useMemo(
      () => filtered.reduce((s, g) => s + (g[type]?.length || 0), 0),
      [filtered, type]
    );

    return (
      <Box mb={4}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={1}
        >
          <Box>
            <Typography variant="h6" fontWeight={800}>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {totalItems} {type} in {filtered.length} departments
            </Typography>
          </Box>

          <TextField
            select
            size="small"
            label="Filter"
            value={selectedDept}
            onChange={(e) => onFilterChange(e.target.value)}
            sx={{ minWidth: 180 }}
          >
            {deptOptions.map((name) => (
              <MenuItem key={name} value={name}>
                {name === "all" ? "All Departments" : name}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Stack spacing={2}>
          {filtered.map((group) => (
            <Paper
              key={group.department.id}
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
              }}
            >
              <Stack direction="row" alignItems="center" gap={1} mb={1}>
                <FolderIcon fontSize="small" color="action" />
                <Typography variant="subtitle1" fontWeight={700}>
                  {group.department.name} — {group[type]?.length || 0} {type}
                </Typography>
              </Stack>

              <Box display="flex" flexWrap="wrap" gap={1}>
                {(group[type] || []).map((item) => (
                  <Chip key={item.id} label={item.name} variant="outlined" />
                ))}
              </Box>
            </Paper>
          ))}

          {filtered.length === 0 && (
            <Box
              sx={{
                display: "grid",
                placeItems: "center",
                p: 3,
                borderRadius: 2,
                border: `1px dashed ${alpha(theme.palette.text.primary, 0.2)}`,
                color: "text.secondary",
              }}
            >
              No data.
            </Box>
          )}
        </Stack>
      </Box>
    );
  };

  /** -------- Loading state or no data yet -------- */
  if (loading || !hasAttemptedFetch || !user) {
    return (
      <Box
        sx={{
          width: "100%",
          minHeight: "100vh",
          pt: "100px",
          px: "32px",
          backgroundColor: theme.palette.background.default,
        }}
      >
        <Stack spacing={3}>
          <Skeleton variant="rounded" height={220} />
          <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
            <Skeleton variant="rounded" height={420} sx={{ flex: 1 }} />
            <Skeleton variant="rounded" height={420} sx={{ flex: 1 }} />
          </Stack>
        </Stack>
      </Box>
    );
  }

  /** -------- Error guard (only show if we have a confirmed error) -------- */
  if (hasError) {
    return (
      <Box textAlign="center" pt="100px">
        <Typography variant="h6" color="error">
          Failed to load profile information.
        </Typography>
        <Button
          startIcon={<RefreshRoundedIcon />}
          sx={{ mt: 2 }}
          onClick={() => fetchUser()}
        >
          Retry
        </Button>
      </Box>
    );
  }

  /** -------- UI -------- */
  const profile = user!.profile || defaultProfile;

  return (
    <Box sx={{ p: 3, background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`, minHeight: '100vh' }}>

      {/* Hero / Header */}
      <Card
        elevation={0}
        sx={{
          mb: 4,
          borderRadius: 4,
          overflow: "hidden",
          border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.8)} 0%, ${alpha(theme.palette.background.paper, 0.95)} 100%)`,
          backdropFilter: 'blur(10px)',
          boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.08)}`,
        }}
      >
        {/* Cover */}
        <Box
          sx={{
            height: 160,
            background:
              theme.palette.mode === "dark"
                ? `linear-gradient(135deg, ${alpha(
                    theme.palette.primary.main,
                    0.3
                  )} 0%, ${alpha(theme.palette.secondary.main, 0.2)} 50%, ${alpha(
                    theme.palette.primary.main,
                    0.1
                  )} 100%)`
                : `linear-gradient(135deg, ${alpha(
                    theme.palette.primary.main,
                    0.2
                  )} 0%, ${alpha(theme.palette.secondary.main, 0.15)} 50%, ${alpha(
                    theme.palette.primary.main,
                    0.08
                  )} 100%)`,
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'radial-gradient(circle at 30% 20%, rgba(255,255,255,0.1) 0%, transparent 50%)',
            }
          }}
        />
        {/* Identity row */}
        <Box sx={{ p: 2, position: "relative" }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={{ mt: "-80px" }}>
              <Avatar
                src={profile.imageUrl || ""}
                sx={{
                  width: 120,
                  height: 120,
                  border: `4px solid ${theme.palette.background.paper}`,
                  boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.15)}`,
                  cursor: isEditing ? "pointer" : "default",
                  transition: 'all 0.3s ease',
                  '&:hover': isEditing ? {
                    transform: 'scale(1.05)',
                    boxShadow: `0 12px 40px ${alpha(theme.palette.common.black, 0.2)}`,
                  } : {},
                }}
                onClick={handleAvatarClick}
              />
              {isEditing && (
                <Tooltip title="Change photo">
                  <IconButton
                    size="small"
                    onClick={handleAvatarClick}
                    sx={{
                      position: "absolute",
                      mt: "-40px",
                      ml: "80px",
                      bgcolor: theme.palette.background.paper,
                      border: `2px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        bgcolor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        transform: 'scale(1.1)',
                      }
                    }}
                  >
                    <CameraAltRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleImageChange}
              />
            </Box>

            <Box sx={{ flex: 1, minWidth: 0, mt: 2 }}>
              <Typography variant="h4" fontWeight={900} noWrap sx={{ 
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1
              }}>
                {profile.name || "—"}
              </Typography>
              <Typography variant="h6" color="text.secondary" noWrap sx={{ mb: 2 }}>
                {profile.title || "—"}
              </Typography>
              <Stack direction="row" spacing={1.5} flexWrap="wrap">
                <Chip
                  size="medium"
                  icon={<VerifiedRoundedIcon />}
                  label={`@${user!.username || getUserInfoFromToken()?.username || "user"}`}
                  variant="filled"
                  sx={{ 
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    fontWeight: 600,
                    '& .MuiChip-icon': {
                      color: theme.palette.primary.main,
                    }
                  }}
                />
                <Chip
                  size="medium"
                  icon={<ShieldMoonRoundedIcon />}
                  label={getUserInfoFromToken()?.role === "admin" ? "Admin" : "Member"}
                  variant="filled"
                  sx={{ 
                    bgcolor: alpha(theme.palette.secondary.main, 0.1),
                    color: theme.palette.secondary.main,
                    fontWeight: 600,
                    '& .MuiChip-icon': {
                      color: theme.palette.secondary.main,
                    }
                  }}
                />
              </Stack>
            </Box>

            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              {!isEditing ? (
                <>
                  <Button
                    variant="contained"
                    startIcon={<EditIcon />}
                    onClick={() => setIsEditing(true)}
                    sx={{
                      borderRadius: 3,
                      px: 3,
                      py: 1.5,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                      boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.4)}`,
                      }
                    }}
                  >
                    Edit Profile
                  </Button>
                  <IconButton 
                    onClick={() => fetchUser()} 
                    title="Refresh"
                    sx={{
                      borderRadius: 3,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.2),
                        transform: 'rotate(180deg)',
                      }
                    }}
                  >
                    <RefreshRoundedIcon />
                  </IconButton>
                </>
              ) : (
                <>
                  <Button
                    variant="contained"
                    startIcon={<SaveRoundedIcon />}
                    onClick={handleSubmit}
                    disabled={submitting}
                    sx={{
                      borderRadius: 3,
                      px: 3,
                      py: 1.5,
                      background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
                      boxShadow: `0 4px 16px ${alpha(theme.palette.success.main, 0.3)}`,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 24px ${alpha(theme.palette.success.main, 0.4)}`,
                      }
                    }}
                  >
                    {submitting ? 'Saving...' : 'Save Changes'}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<CloseRoundedIcon />}
                    onClick={() => {
                      setIsEditing(false);
                      setSelectedImage(null);
                      fetchUser(); // revert
                    }}
                    sx={{
                      borderRadius: 3,
                      px: 3,
                      py: 1.5,
                      borderColor: alpha(theme.palette.error.main, 0.3),
                      color: theme.palette.error.main,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: theme.palette.error.main,
                        bgcolor: alpha(theme.palette.error.main, 0.1),
                        transform: 'translateY(-2px)',
                      }
                    }}
                  >
                    Cancel
                  </Button>
                </>
              )}
            </Stack>
          </Stack>

          {/* KPI trio */}
          <Stack
            direction="row"
            spacing={3}
            mt={4}
            flexWrap="wrap"
            useFlexGap
          >
            <Paper
              elevation={0}
              sx={{
                px: 3,
                py: 2.5,
                minWidth: 180,
                borderRadius: 4,
                textAlign: "center",
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.12)} 100%)`,
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
                }
              }}
            >
              <Typography variant="body2" color="primary" fontWeight={600} sx={{ mb: 1 }}>
                🧾 Positions
              </Typography>
              <Typography variant="h4" fontWeight={900} color="primary">
                {totalPositions}
              </Typography>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                px: 3,
                py: 2.5,
                minWidth: 180,
                borderRadius: 4,
                textAlign: "center",
                border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.08)} 0%, ${alpha(theme.palette.success.main, 0.12)} 100%)`,
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 8px 32px ${alpha(theme.palette.success.main, 0.15)}`,
                  border: `1px solid ${alpha(theme.palette.success.main, 0.4)}`,
                }
              }}
            >
              <Typography variant="body2" color="success.main" fontWeight={600} sx={{ mb: 1 }}>
                📁 Projects
              </Typography>
              <Typography variant="h4" fontWeight={900} color="success.main">
                {totalProjects}
              </Typography>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                px: 3,
                py: 2.5,
                minWidth: 180,
                borderRadius: 4,
                textAlign: "center",
                border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.08)} 0%, ${alpha(theme.palette.warning.main, 0.12)} 100%)`,
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 8px 32px ${alpha(theme.palette.warning.main, 0.15)}`,
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.4)}`,
                }
              }}
            >
              <Typography variant="body2" color="warning.main" fontWeight={600} sx={{ mb: 1 }}>
                🏢 Departments
              </Typography>
              <Typography variant="h4" fontWeight={900} color="warning.main">
                {uniqueDepartmentsCount}
              </Typography>
            </Paper>
          </Stack>
        </Box>
      </Card>

      {/* Content */}
      <Stack direction={{ xs: "column", lg: "row" }} spacing={4}>
        {/* Left: profile form */}
        <Card
          component="form"
          onSubmit={handleSubmit}
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 4,
            flex: "1 1 420px",
            border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
            background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.8)} 0%, ${alpha(theme.palette.background.paper, 0.95)} 100%)`,
            backdropFilter: 'blur(10px)',
            boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.08)}`,
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: `0 12px 40px ${alpha(theme.palette.common.black, 0.12)}`,
            }
          }}
        >
          <Typography variant="h6" fontWeight={900} sx={{ mb: 2, 
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}>
            Personal Information
          </Typography>
          <Divider sx={{ mb: 3, 
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.2)} 0%, ${alpha(theme.palette.secondary.main, 0.2)} 100%)`,
            height: 2,
            border: 'none',
          }} />

          <Stack spacing={1.75}>
            <TextField
              fullWidth
              size="small"
              name="name"
              label="Name"
              value={profile.name}
              onChange={handleChange}
              disabled={!isEditing}
              required
            />
            <TextField
              fullWidth
              size="small"
              name="title"
              label="Title"
              value={profile.title}
              onChange={handleChange}
              disabled={!isEditing}
            />

            <Stack direction="row" spacing={2}>
              <TextField
                fullWidth
                size="small"
                name="age"
                label="Age"
                value={profile.age ?? ""}
                onChange={handleChange}
                disabled={!isEditing}
                inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
              />
              <TextField
                fullWidth
                size="small"
                name="gender"
                label="Gender"
                value={profile.gender}
                onChange={handleChange}
                disabled={!isEditing}
              />
            </Stack>

            <TextField
              fullWidth
              size="small"
              name="email"
              label="Email"
              value={profile.email}
              onChange={handleChange}
              disabled={!isEditing}
              type="email"
            />
            <TextField
              fullWidth
              size="small"
              name="phone"
              label="Phone"
              value={profile.phone}
              onChange={handleChange}
              disabled={!isEditing}
            />

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Date of Birth"
                value={dateOfBirth}
                onChange={(v) => setDateOfBirth(v)}
                disabled={!isEditing}
                slotProps={{
                  textField: { fullWidth: true, size: "small" },
                }}
              />
            </LocalizationProvider>

            <TextField
              fullWidth
              size="small"
              name="address"
              label="Address"
              value={profile.address}
              onChange={handleChange}
              disabled={!isEditing}
            />
            <TextField
              fullWidth
              size="small"
              name="notes"
              label="Notes"
              multiline
              minRows={3}
              value={profile.notes}
              onChange={handleChange}
              disabled={!isEditing}
            />

            {isEditing && (
              <Stack direction="row" spacing={1} pt={1}>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SaveRoundedIcon />}
                  disabled={submitting}
                >
                  Save Changes
                </Button>
                <Button
                  variant="text"
                  color="secondary"
                  startIcon={<CloseRoundedIcon />}
                  onClick={() => {
                    setIsEditing(false);
                    setSelectedImage(null);
                    fetchUser();
                  }}
                >
                  Cancel
                </Button>
              </Stack>
            )}
          </Stack>
        </Card>

        {/* Right: assignments with tabs */}
        <Card
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 4,
            flex: "1 1 520px",
            border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
            background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.8)} 0%, ${alpha(theme.palette.background.paper, 0.95)} 100%)`,
            backdropFilter: 'blur(10px)',
            boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.08)}`,
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: `0 12px 40px ${alpha(theme.palette.common.black, 0.12)}`,
            }
          }}
        >
          <Typography variant="h6" fontWeight={900} sx={{ mb: 2, 
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}>
            User Assignments
          </Typography>
          <Divider sx={{ mb: 2, 
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.2)} 0%, ${alpha(theme.palette.secondary.main, 0.2)} 100%)`,
            height: 2,
            border: 'none',
          }} />

          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="scrollable"
            allowScrollButtonsMobile
            sx={{ 
              mb: 3,
              '& .MuiTab-root': {
                borderRadius: 3,
                mx: 0.5,
                transition: 'all 0.3s ease',
                '&.Mui-selected': {
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
                  color: theme.palette.primary.main,
                  fontWeight: 700,
                }
              },
              '& .MuiTabs-indicator': {
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                height: 3,
                borderRadius: 2,
              }
            }}
          >
            <Tab label="Positions" />
            <Tab label="Projects" />
          </Tabs>

          {tab === 0 && (
            <AssignmentSection
              title="Positions by Department"
              data={positionsByDept}
              type="positions"
              selectedDept={selectedPositionDept}
              onFilterChange={setSelectedPositionDept}
            />
          )}

          {tab === 1 && (
            <AssignmentSection
              title="Projects by Department"
              data={projectsByDept}
              type="projects"
              selectedDept={selectedProjectDept}
              onFilterChange={setSelectedProjectDept}
            />
          )}
        </Card>
      </Stack>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5500}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Profile;
